import { ADDRESS_NODE_TYPES } from '../static/tracerConsts';

import { orderLabels } from 'blockscope/components/blockscopeCommon/utils';
import { extractAddress } from 'blockscope/utils/helperUtils';

/**
 *
 * @param {import('./tracerStore').TracerNode[]} nodes
 * @param {import('./tracerStore').TracerEdge[]} edges
 * @param {import('./tracerStore').InterestedAddressObject[]} interestedAddresses
 * @param {import('stateManagement/queries/services/tracer/zod').TracerSettings} settings
 * @param {String} alias
 * @param {String} [tracerId]
 * @returns {import('../../../../stateManagement/queries/services/tracer/zod').BaseTracer &{tracerId:string}}
 */
export const getSaveableTracerObject = (
  nodes,
  edges,
  interestedAddresses,
  settings,
  alias,
  tracerId
) => {
  const tracerObject = {
    nodes: nodes.map((node) => {
      const toSave = {
        nodeId: node.id,
        type: node.type,
        savedPosition: node.position || node.savedPosition || { x: 0, y: 0 },
        savedSize: {
          height: node.height || 50,
          width: node.width || 200,
        },
        data: {
          members: node.data.members?.map((member) => ({
            ...member,
            nodeId: member.id,
            savedPosition: member.position ||
              member.savedPosition || { x: 0, y: 0 },
            data: {
              ...member.data,
              address: extractAddress(member.data.address),
              searchParams: member.data.searchParams?.map((param) => ({
                ...param,
                tokenAddress: extractAddress(param.tokenAddress || param.token),
                filterAddress: extractAddress(param.filterAddress),
              })),
            },
          })),
          internalEdges: node.data.internalEdges?.map((edge) => {
            const internalEdgeToSave = { ...edge, edgeId: edge.id };
            const { queries } = edge.data;
            if (queries) {
              const queriesToSave = {};
              for (const queryKey in queries) {
                queriesToSave[queryKey] = {
                  ...queries[queryKey],
                  amount: queries[queryKey].amount.raw,
                  tokenAddress: extractAddress(
                    queries[queryKey].tokenAddress || queries[queryKey].token
                  ),
                  sourceAddress: extractAddress(
                    queries[queryKey].sourceAddress
                  ),
                  targetAddress: extractAddress(
                    queries[queryKey].targetAddress
                  ),
                  filterAddress: extractAddress(
                    queries[queryKey].filterAddress
                  ),
                  txnTime: queries[queryKey].txnTime,
                };
              }

              internalEdgeToSave.data.queries = queriesToSave;
            }
            return internalEdgeToSave;
          }),
          searchParams: node.data.searchParams?.map((param) => ({
            ...param,
            tokenAddress: extractAddress(param.tokenAddress || param.token),
            filterAddress: extractAddress(param.filterAddress),
          })),
          chain: node.data.chain,

          interested: node.data.interested,
          addressNodeType: node.data.addressNodeType,
          name: node.data.name,
          text: node.data.text,
        },
      };
      if (node.data.address) {
        toSave.data.address = extractAddress(node.data.address);
        if (!node.data.addressNodeType) {
          const { labels } = node.data.address;
          if (labels.length > 0) {
            const orderedLabels = orderLabels(labels);
            for (const label of orderedLabels) {
              if (Object.values(ADDRESS_NODE_TYPES).includes(label.labelType)) {
                toSave.data.addressNodeType = label.labelType;
                break;
              }
            }
          }
        }
      }

      return toSave;
    }),
    edges: edges.map((edge) => {
      const { members, queries } = edge.data;
      const edgeToSave = {
        edgeId: edge.id,
        type: edge.type,
        source: edge.source,
        target: edge.target,
        data: {},
      };

      if (queries) {
        const queriesToSave = {};
        for (const queryKey in queries) {
          queriesToSave[queryKey] = {
            ...queries[queryKey],
            amount: queries[queryKey].amount.raw,
            tokenAddress: extractAddress(
              queries[queryKey].tokenAddress || queries[queryKey].token
            ),
            sourceAddress: extractAddress(queries[queryKey].sourceAddress),
            targetAddress: extractAddress(queries[queryKey].targetAddress),
            filterAddress: extractAddress(queries[queryKey].filterAddress),
            txnTime: queries[queryKey].txnTime,
          };
        }

        edgeToSave.data.queries = queriesToSave;
      }

      if (members) {
        const membersToSave = members.map((member) => {
          const memberToSave = {
            id: member.id,
            type: member.type,
            source: member.source,
            target: member.target,
            data: {},
          };
          const { queries: memberQueries } = member.data;
          const queriesToSave = {};
          for (const queryKey in memberQueries) {
            queriesToSave[queryKey] = {
              ...memberQueries[queryKey],
              amount: memberQueries[queryKey].amount.raw,
              tokenAddress: extractAddress(
                memberQueries[queryKey].tokenAddress ||
                  memberQueries[queryKey].token
              ),
              sourceAddress: extractAddress(
                memberQueries[queryKey].sourceAddress
              ),
              targetAddress: extractAddress(
                memberQueries[queryKey].targetAddress
              ),
              filterAddress: extractAddress(
                memberQueries[queryKey].filterAddress
              ),
              txnTime: memberQueries[queryKey].txnTime,
            };
          }

          memberToSave.data.queries = queriesToSave;
          return memberToSave;
        });

        edgeToSave.data.members = membersToSave;
      }
      return edgeToSave;
    }),
    alias,
    tracerId,
    settings,
    interestedAddresses: interestedAddresses.map((ia) => ({
      chain: ia.chain,
      address: extractAddress(ia.address),
    })),
  };

  return tracerObject;
};
